import { ComponentClassNames } from '../../util/classNames';
import { Depth, getDepthBoxShadow } from '../../util/styles/depth';
import { ZIndex } from '../../util/styles/zIndex';
import { ITheme, memoizeFunction, mergeStyleSets } from '../../util/styling';

import { PageLayoutWidth } from './PageLayout.types';

export const leftSidebarLeftMargin = 26;

export const minLeftSidebarWidth = 149;
export const maxLeftSidebarWidth = 249;

export const maxCenterColumnWidth = 680;
export const centerColumnVerticalGutter = 12;

export const rightSidebarWidthNarrow = 292;
export const rightSidebarWidthWide = 320;

export const horizontalMargin = 10;
export const rightSidebarPadding = 16;

export const minLeftSidebarRightMargin = 26 - horizontalMargin;
export const minRightSidebarLeftMargin = 26 - horizontalMargin - rightSidebarPadding;
export const maxRightSidebarLeftMargin = 48 - horizontalMargin - rightSidebarPadding;
export const rightSidebarRightMargin = 25 - rightSidebarPadding;

// Content = CenterColumn + RightSidebar
export const defaultHeaderHeight = 48;
export const defaultBannerHeight = 32;
export const defaultFooterHeight = 38;
export const verticalGutterHeight = 24;
export const defaultHeaderHeightWithSpacing = defaultHeaderHeight + verticalGutterHeight;

export const buttonLength = 44;

export const noScrollClass = 'noScroll';

// calculating leftSidebar and contentColumn width (when rightSidebar is collapsed) percentages
const leftSidebarWithMargins = leftSidebarLeftMargin + maxLeftSidebarWidth + minLeftSidebarRightMargin;
const contentColumnWithMargins =
  maxCenterColumnWidth + 2 * horizontalMargin + rightSidebarWidthNarrow + rightSidebarRightMargin;
export const totalWidth = leftSidebarWithMargins + contentColumnWithMargins;
const contentColumnWidthPercent = 100 * (contentColumnWithMargins / totalWidth);

// IE11 has a different default value for "flex" and doesn't accept "auto" for flex-basis, so it's
// necessary to specifically define "flex-grow: 1, flex-shrink: 1, and flex-bases: 100%".
// https://caniuse.com/#feat=flexbox
export const flexibleFlexStyleIE11Compatible = '1 1 100%';

const getMaxContentColumnWidth = (showLeftSidebar: boolean, width?: PageLayoutWidth) => {
  const availableLeftSidebarWidth = showLeftSidebar ? 0 : leftSidebarWithMargins;
  const maxContentWidth =
    availableLeftSidebarWidth +
    maxCenterColumnWidth +
    horizontalMargin * 2 +
    rightSidebarWidthNarrow +
    maxRightSidebarLeftMargin +
    rightSidebarRightMargin;

  switch (width) {
    case PageLayoutWidth.Large:
      return maxContentWidth + minRightSidebarLeftMargin - maxRightSidebarLeftMargin;
    case PageLayoutWidth.XLarge:
      return maxContentWidth;
    default:
      return contentColumnWithMargins;
  }
};

const getFullWidthBoxShadow = (fullWidthLayout: boolean | undefined, theme: ITheme, showLeftSidebar: boolean) => {
  if (!fullWidthLayout || theme.isInverted || !showLeftSidebar) {
    return undefined;
  }

  return getDepthBoxShadow(Depth.depth6);
};

interface GetClassNamesProps {
  readonly fullWidthLayout?: boolean;
  readonly noSpacing: boolean;
  readonly supressNoScrollRightPadding: boolean;
  readonly showLeftSidebar: boolean;
  readonly theme: ITheme;
  readonly width?: PageLayoutWidth;
}

type PageLayoutClassNames = ComponentClassNames<'pageLayout' | 'contentWrapper' | 'contentColumn'>;

export const getClassNames = memoizeFunction(
  ({
    fullWidthLayout,
    noSpacing,
    supressNoScrollRightPadding,
    showLeftSidebar,
    theme,
    width,
  }: GetClassNamesProps): PageLayoutClassNames => {
    const isSmall = width === PageLayoutWidth.Small;
    const contentColumnWidth = isSmall ? '100%' : `${contentColumnWidthPercent}%`;
    const maxContentColumnWidth = noSpacing ? undefined : getMaxContentColumnWidth(showLeftSidebar, width);
    const scrollbarWidth = supressNoScrollRightPadding
      ? 0
      : Math.max(0, window.innerWidth - document.documentElement.clientWidth);

    return mergeStyleSets({
      pageLayout: {
        display: 'flex',
        justifyContent: 'center',
        // Only this container can have a min-height, IE11 will improperly calculate child sizes if a child
        // has min-height but no height value.
        // https://caniuse.com/#feat=flexbox
        minHeight: '100vh',
        selectors: {
          ':global(body)': {
            overflowY: 'scroll',
          },
          [`:global(body.${noScrollClass})`]: {
            overflowY: 'hidden',
            paddingRight: scrollbarWidth,
          },
        },
      },
      contentWrapper: {
        display: 'flex',
        justifyContent: fullWidthLayout ? undefined : 'center',
        width: '100%',
      },
      contentColumn: {
        flex: flexibleFlexStyleIE11Compatible,
        width: contentColumnWidth,
        maxWidth: fullWidthLayout ? undefined : maxContentColumnWidth,
        zIndex: ZIndex.PageLayoutContentColumn,
        boxShadow: getFullWidthBoxShadow(fullWidthLayout, theme, showLeftSidebar),
      },
    });
  }
);
